/* Product page: variant choice, quantity, Add to bag, the photo lightbox (loaded on first open), the phone gallery
   counter and the phone sticky bar.
   Add to bag posts the product form to Shopify's standard /cart/add.js (through LMB.cart in assets/cart.js, which
   updates and opens the bag), so the product_added_to_cart customer event fires for every pixel. Without JavaScript
   the form posts to /cart/add. */
if (!customElements.get('lmb-product')) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const t = (key, fallback) => (window.LMB && LMB.strings && LMB.strings[key]) || fallback;
  const PHONE = window.matchMedia('(max-width: 749px)');

  /* Formats cents with the shop's money format, dropping ".00" like money_without_trailing_zeros. */
  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const withDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const trim = (amount, sep) => (decimals === '00' ? amount : `${amount}${sep}${decimals}`);
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withCommas;
        case 'amount_with_comma_separator':
          return trim(withDots, ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDots;
        default:
          return trim(withCommas, '.');
      }
    });
  };

  /* Loads the lightbox script and stylesheet once, on the first open. */
  let lightboxPromise = null;
  const loadLightbox = (scriptSrc, styleHref) => {
    if (window.LMB && LMB.lightbox) return Promise.resolve();
    if (!lightboxPromise) {
      const load = (el) =>
        new Promise((resolve, reject) => {
          el.onload = resolve;
          el.onerror = reject;
          document.head.appendChild(el);
        });
      const style = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: styleHref });
      const script = Object.assign(document.createElement('script'), { src: scriptSrc });
      lightboxPromise = Promise.all([load(style), load(script)]).catch((error) => {
        lightboxPromise = null;
        throw error;
      });
    }
    return lightboxPromise;
  };

  customElements.define(
    'lmb-product',
    class LmbProduct extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.addButton = this.querySelector('[data-add-button]');
        this.stickyButton = this.querySelector('[data-sticky-add]');
        this.qtyInput = this.querySelector('[data-qty-input]');
        this.status = this.querySelector('[data-form-status]');
        const variantsScript = this.querySelector('[data-variants]');
        this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : null;
        this.variant = null;
        this.unitPrice = this.readUnitPrice();

        this.addEventListener('click', (event) => {
          const step = event.target.closest('[data-qty-step]');
          if (step) this.stepQuantity(Number(step.dataset.qtyStep));
          const zoom = event.target.closest('[data-lightbox]');
          if (zoom) this.openLightbox(Number(zoom.dataset.lightbox), zoom);
        });
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-option-index]')) this.onOptionChange();
          if (event.target === this.qtyInput) this.updateAddPrice();
        });
        if (this.qtyInput) this.qtyInput.addEventListener('input', () => this.updateAddPrice());
        if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));

        this.initGallery();
        this.initSticky();
      }

      disconnectedCallback() {
        if (this.stickyObserver) this.stickyObserver.disconnect();
      }

      readUnitPrice() {
        if (this.variants) {
          const id = Number(this.form && this.form.querySelector('[data-variant-id]').value);
          const variant = this.variants.find((v) => v.id === id);
          if (variant) return variant.price;
        }
        const el = this.querySelector('[data-unit-price]');
        return el ? Number(el.dataset.unitPrice) : null;
      }

      get quantity() {
        const value = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
        return Number.isFinite(value) && value > 0 ? value : 1;
      }

      stepQuantity(delta) {
        if (!this.qtyInput) return;
        const max = Number(this.qtyInput.max) || 99;
        this.qtyInput.value = Math.min(max, Math.max(1, this.quantity + delta));
        this.updateAddPrice();
      }

      updateAddPrice() {
        const priceEl = this.querySelector('[data-add-price]');
        if (!priceEl || this.unitPrice == null) return;
        const total = formatMoney(this.unitPrice * this.quantity, this.dataset.moneyFormat);
        priceEl.firstChild.textContent = `${total} `;
      }

      /* ---------- Variants ---------- */
      onOptionChange() {
        if (!this.variants) return;
        const picker = this.querySelector('[data-variant-picker]');
        const selected = [];
        picker.querySelectorAll('[data-option-index]:checked').forEach((input) => {
          selected[Number(input.dataset.optionIndex)] = input.value;
        });
        const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
        this.setVariant(variant);
      }

      setVariant(variant) {
        const idInput = this.form && this.form.querySelector('[data-variant-id]');
        const available = !!(variant && variant.available);
        if (variant && idInput) idInput.value = variant.id;
        if (variant) {
          this.unitPrice = variant.price;
          const price = this.querySelector('[data-price]');
          if (price) price.innerHTML = variant.price_html;
          const sticky = this.querySelector('[data-sticky-price]');
          if (sticky) sticky.textContent = formatMoney(variant.price, this.dataset.moneyFormat);
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
        const label = available ? t('add_to_bag', 'Add to bag') : variant ? t('sold_out', 'Sold out') : t('unavailable', 'Unavailable');
        [this.addButton, this.stickyButton].forEach((button) => {
          if (!button) return;
          button.disabled = !available;
          const text = button.querySelector('[data-add-label]') || button;
          text.textContent = label;
        });
        const dynamic = this.querySelector('[data-dynamic-checkout]');
        if (dynamic) dynamic.classList.toggle('is-hidden', !available);
        this.updateAddPrice();
      }

      /* ---------- Add to bag ---------- */
      async onSubmit(event) {
        event.preventDefault();
        if (!this.addButton || this.addButton.disabled || this.addButton.getAttribute('aria-busy') === 'true') return;
        const buttons = [this.addButton, this.stickyButton].filter(Boolean);
        buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
        this.setStatus('');
        try {
          const submitter = event.submitter && event.submitter !== this.addButton ? event.submitter : this.addButton;
          if (window.LMB && LMB.cart) {
            // assets/cart.js: /cart/add.js with the bag rendered in the same request; opens the bag.
            await LMB.cart.add(new FormData(this.form), { trigger: submitter });
          } else {
            const response = await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: new FormData(this.form),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.description || data.message || t('quick_add_error', 'Could not add this piece.'));
            const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
            if (window.LMB && LMB.updateCartCount) LMB.updateCartCount(cart.item_count);
          }
          this.setStatus(t('quick_add_added', 'Added to bag'));
          buttons.forEach((b) => b.classList.add('is-added'));
          setTimeout(() => buttons.forEach((b) => b.classList.remove('is-added')), 1800);
        } catch (error) {
          this.setStatus(error.message, true);
        } finally {
          buttons.forEach((b) => b.removeAttribute('aria-busy'));
        }
      }

      setStatus(message, isError = false) {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.classList.toggle('is-error', isError);
      }

      /* ---------- Lightbox: its script and styles load on the first open only ---------- */
      async openLightbox(start, trigger) {
        if (!this.dataset.lightboxScript || this.lightboxLoading) return;
        this.lightboxLoading = true;
        try {
          await loadLightbox(this.dataset.lightboxScript, this.dataset.lightboxStyle);
        } catch (error) {
          return;
        } finally {
          this.lightboxLoading = false;
        }
        const items = Array.from(this.querySelectorAll('[data-lightbox]')).map((button) => {
          const image = button.querySelector('img');
          return {
            srcset: button.dataset.srcset,
            width: Number(button.dataset.width),
            height: Number(button.dataset.height),
            alt: image ? image.alt : '',
          };
        });
        let strings = {};
        try {
          strings = JSON.parse(this.dataset.lightboxStrings || '{}');
        } catch (error) {
          /* default labels */
        }
        LMB.lightbox.open({ items, start, trigger, strings, scheme: this.dataset.lightboxScheme });
      }

      /* ---------- Phone gallery: "01 / 04" follows the swipe ---------- */
      initGallery() {
        const list = this.querySelector('[data-gallery]');
        const current = this.querySelector('[data-gallery-current]');
        if (!list || !current) return;
        const items = Array.from(list.querySelectorAll('[data-media-index]'));
        let frame = null;
        const update = () => {
          frame = null;
          const left = list.getBoundingClientRect().left;
          let index = 0;
          let best = Infinity;
          items.forEach((item, i) => {
            const distance = Math.abs(item.getBoundingClientRect().left - left);
            if (distance < best) {
              best = distance;
              index = i;
            }
          });
          current.textContent = String(index + 1).padStart(2, '0');
        };
        list.addEventListener(
          'scroll',
          () => {
            if (!frame) frame = requestAnimationFrame(update);
          },
          { passive: true }
        );
      }

      /* ---------- Phone sticky bar: shown once the main buttons are above the viewport ---------- */
      initSticky() {
        const bar = this.querySelector('[data-sticky-atc]');
        const target = this.querySelector('[data-buy]');
        if (!bar || !target || !('IntersectionObserver' in window)) return;
        let visible = false;
        const show = (value) => {
          if (value === visible) return;
          visible = value;
          if (value) {
            bar.hidden = false;
            requestAnimationFrame(() => bar.classList.add('is-visible'));
          } else {
            bar.classList.remove('is-visible');
            setTimeout(() => {
              if (!visible) bar.hidden = true;
            }, 260);
          }
          document.documentElement.classList.toggle('has-sticky-atc', value);
        };
        // The observed area reaches far below the screen, so the buttons count as "in view" until they pass the top
        // edge, even when a fast scroll jumps right over the screen.
        this.stickyObserver = new IntersectionObserver(
          ([entry]) => {
            show(PHONE.matches && !entry.isIntersecting);
          },
          { rootMargin: '0px 0px 100000px 0px' }
        );
        this.stickyObserver.observe(target);
        PHONE.addEventListener('change', () => {
          if (!PHONE.matches) show(false);
        });
      }
    }
  );
}
